import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useConfirm } from "./ConfirmModal";
import Icons from "../icons";
import HelpButton from "./HelpDialog";
import ScheduleWizard from "./ScheduleWizard";
import { DIALOG_HEIGHT } from "./dialogSize";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/* Colour mapping for schedule / occurrence statuses. */
const STATUS_META = {
  completed: { label: "Completed", badge: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", dot: "bg-emerald-500" },
  passed: { label: "Passed", badge: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", dot: "bg-emerald-500" },
  running: { label: "Running", badge: "bg-sky-50 text-sky-700 ring-sky-600/20", dot: "bg-sky-500" },
  partial: { label: "Partial", badge: "bg-amber-50 text-amber-700 ring-amber-600/20", dot: "bg-amber-500" },
  skipped: { label: "Skipped", badge: "bg-slate-100 text-slate-600 ring-slate-500/20", dot: "bg-slate-400" },
  failed: { label: "Failed", badge: "bg-rose-50 text-rose-700 ring-rose-600/20", dot: "bg-rose-500" },
  error: { label: "Error", badge: "bg-rose-50 text-rose-700 ring-rose-600/20", dot: "bg-rose-500" },
};

function StatusPill({ status }) {
  if (!status) return <span className="text-sm text-slate-400">—</span>;
  const meta = STATUS_META[status] || {
    label: status,
    badge: "bg-slate-100 text-slate-600 ring-slate-500/20",
    dot: "bg-slate-400",
  };
  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
        meta.badge,
      ].join(" ")}
    >
      <span className={["h-1.5 w-1.5 rounded-full", meta.dot].join(" ")} />
      {meta.label}
    </span>
  );
}

/* Modal shell — used by the run-history and audit views. */
function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 sm:p-8">
      <div
        className={[
          `flex w-full flex-col overflow-hidden ${DIALOG_HEIGHT} rounded-2xl bg-card shadow-xl ring-1 ring-slate-200`,
          wide ? "max-w-3xl" : "max-w-2xl",
        ].join(" ")}
      >
        <div className="flex flex-none items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-base font-semibold text-slate-800">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

export default function SchedulingView({ boards = [], userkey = null }) {
  const [ask, confirmDialog] = useConfirm();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  // Wizard host: null = list; otherwise { schedule: null|object } (null => create).
  const [wizard, setWizard] = useState(null);

  // Runs / audit modals
  const [runsFor, setRunsFor] = useState(null);
  const [runs, setRuns] = useState([]);
  const [auditFor, setAuditFor] = useState(null);
  const [audit, setAudit] = useState([]);

  const flash = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  }, []);

  const loadSchedules = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}schedules`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setSchedules(Array.isArray(body?.data) ? body.data : []);
    } catch (e) {
      setError(e.message || "Failed to load schedules.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  const toggleEnabled = async (s) => {
    const action = s.enabled ? "disable" : "enable";
    try {
      const res = await fetch(`${API_BASE_URL}schedules/${s.schedulekey}/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userkey: userkey || null }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      flash(s.enabled ? "Schedule disabled." : "Schedule enabled.");
      loadSchedules();
    } catch (err) {
      flash(`Could not ${action}: ${err.message}`);
    }
  };

  const runNow = async (s) => {
    try {
      const res = await fetch(`${API_BASE_URL}schedules/${s.schedulekey}/run-now`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userkey: userkey || null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      flash(`Run started for "${s.name}".`);
    } catch (err) {
      flash(`Could not start run: ${err.message}`);
    }
  };

  const remove = async (s) => {
    if (!(await ask({ title: "Delete schedule", message: `Delete schedule "${s.name}"? This cannot be undone.` }))) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}schedules/${s.schedulekey}?userkey=${encodeURIComponent(userkey || "")}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      flash("Schedule deleted.");
      loadSchedules();
    } catch (err) {
      flash(`Could not delete: ${err.message}`);
    }
  };

  const openRuns = async (s) => {
    setRunsFor(s);
    setRuns([]);
    try {
      const res = await fetch(`${API_BASE_URL}schedules/${s.schedulekey}/runs`);
      const body = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(body?.data)) setRuns(body.data);
    } catch {
      /* leave empty */
    }
  };

  const openAudit = async (s) => {
    setAuditFor(s);
    setAudit([]);
    try {
      const res = await fetch(`${API_BASE_URL}schedules/${s.schedulekey}/audit`);
      const body = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(body?.data)) setAudit(body.data);
    } catch {
      /* leave empty */
    }
  };

  const boardName = (id) => boards.find((b) => b.id === id)?.name || id || "—";

  const targetSummary = (s) => {
    if (s.target_type === "board") return `Whole test suite · ${boardName(s.board_id)}`;
    if (s.target_type === "group") return "Test-case sub-category";
    const n = (s.target_testcasekeys || []).length;
    return `${n} test case${n === 1 ? "" : "s"}`;
  };

  const sorted = useMemo(
    () => [...schedules].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [schedules]
  );

  // ---- Wizard (create / edit) takes over the page, like Create report ---- //
  if (wizard) {
    return (
      <ScheduleWizard
        boards={boards}
        userkey={userkey}
        schedule={wizard.schedule}
        onCancel={() => setWizard(null)}
        onSaved={(msg) => {
          setWizard(null);
          flash(msg);
          loadSchedules();
        }}
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-800">Test Scheduling</h1>
          <p className="text-sm text-slate-500">
            Run test cases and suites automatically — one-off, recurring or on a cron expression.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadSchedules}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            <Icons.Refresh className="h-4 w-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setWizard({ schedule: null })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800"
          >
            <Icons.Plus className="h-4 w-4" />
            New schedule
          </button>
          <HelpButton screen="scheduling" />
        </div>
      </div>

      {toast && (
        <div className="mb-4 rounded-lg border border-brand-200 bg-brand-50 px-4 py-2.5 text-sm text-brand-800">
          {toast}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
          <Icons.Spinner className="h-5 w-5 animate-spin" />
          Loading schedules…
        </div>
      ) : error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : sorted.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-card px-6 py-16 text-center">
          <p className="text-sm font-medium text-slate-600">No schedules yet</p>
          <p className="mt-1 text-sm text-slate-400">
            Create a schedule to run your test cases automatically.
          </p>
          <button
            type="button"
            onClick={() => setWizard({ schedule: null })}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800"
          >
            <Icons.Plus className="h-4 w-4" />
            New schedule
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left">
              <thead>
                <tr className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500 shadow-[inset_0_-1px_0_rgb(226_232_240)]">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Target</th>
                  <th className="px-4 py-3">Cadence</th>
                  <th className="px-4 py-3">Next run</th>
                  <th className="px-4 py-3">Last status</th>
                  <th className="px-4 py-3">Enabled</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((s) => (
                  <tr key={s.schedulekey} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-slate-800">{s.name}</div>
                      {s.description && (
                        <div className="max-w-xs truncate text-xs text-slate-400">{s.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{targetSummary(s)}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-slate-700">{s.cadence}</div>
                      <div className="text-xs text-slate-400">{s.timezone}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {s.enabled ? fmt(s.next_run_at) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3"><StatusPill status={s.last_status} /></td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleEnabled(s)}
                        role="switch"
                        aria-checked={s.enabled}
                        title={s.enabled ? "Disable" : "Enable"}
                        className={[
                          "relative inline-flex h-5 w-9 items-center rounded-full transition",
                          s.enabled ? "bg-brand-600" : "bg-slate-300",
                        ].join(" ")}
                      >
                        <span
                          className={[
                            "inline-block h-4 w-4 transform rounded-full bg-white shadow transition",
                            s.enabled ? "translate-x-4" : "translate-x-0.5",
                          ].join(" ")}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <IconBtn title="Run now" onClick={() => runNow(s)}>
                          <Icons.Play className="h-4 w-4" />
                        </IconBtn>
                        <IconBtn title="Run history" onClick={() => openRuns(s)}>
                          <Icons.Clock className="h-4 w-4" />
                        </IconBtn>
                        <IconBtn title="Audit trail" onClick={() => openAudit(s)}>
                          <Icons.Eye className="h-4 w-4" />
                        </IconBtn>
                        <IconBtn title="Edit" onClick={() => setWizard({ schedule: s })}>
                          <Icons.Pencil className="h-4 w-4" />
                        </IconBtn>
                        <IconBtn title="Delete" danger onClick={() => remove(s)}>
                          <Icons.Trash className="h-4 w-4" />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Run history modal */}
      {runsFor && (
        <Modal title={`Run history — ${runsFor.name}`} onClose={() => setRunsFor(null)} wide>
          {runs.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">No runs recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead>
                  <tr className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-4">Fired</th>
                    <th className="py-2 pr-4">Trigger</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Passed</th>
                    <th className="py-2 pr-4">Failed</th>
                    <th className="py-2 pr-4">Build</th>
                    <th className="py-2 pr-4">Notified</th>
                    <th className="py-2 pr-4">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.schedulerunkey} className="border-t border-slate-100 align-top">
                      <td className="py-2 pr-4 text-sm text-slate-600">{fmt(r.fired_at)}</td>
                      <td className="py-2 pr-4 text-sm text-slate-500">{r.trigger}</td>
                      <td className="py-2 pr-4"><StatusPill status={r.status} /></td>
                      <td className="py-2 pr-4 text-sm text-emerald-600">{r.passed}</td>
                      <td className="py-2 pr-4 text-sm text-rose-600">{r.failed || "—"}</td>
                      <td className="py-2 pr-4 text-sm text-slate-500">{r.build || "—"}</td>
                      <td className="py-2 pr-4 text-sm text-slate-500">{r.notified ? "Yes" : "—"}</td>
                      <td className="py-2 pr-4 text-xs text-slate-500">
                        {r.detail ? (
                          <pre className="max-w-md whitespace-pre-wrap break-words font-sans text-rose-600">
                            {r.detail}
                          </pre>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}

      {/* Audit trail modal */}
      {auditFor && (
        <Modal title={`Audit trail — ${auditFor.name}`} onClose={() => setAuditFor(null)} wide>
          {audit.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">No audit entries.</p>
          ) : (
            <ul className="space-y-3">
              {audit.map((a) => (
                <li key={a.auditkey} className="flex items-start gap-3 rounded-lg border border-slate-200 p-3">
                  <span className="mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-slate-100 text-slate-500">
                    <Icons.Check className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium capitalize text-slate-800">{a.action}</span>
                      <span className="text-xs text-slate-400">{fmt(a.createddate)}</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      by {a.changed_by_name || "system"}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
      {/* The questions handlers ask before they act (useConfirm). */}
      {confirmDialog}
    </div>
  );
}

function IconBtn({ title, onClick, danger, children }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={[
        "rounded-lg p-1.5 transition",
        danger
          ? "text-slate-400 hover:bg-rose-50 hover:text-rose-600"
          : "text-slate-400 hover:bg-slate-100 hover:text-slate-700",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
